// src/forms/data_hub_metadata.js
// Extracted from data_hub.js — metadata loading and page metadata defaults.

import { fetch_with_timeout } from '../helpers/index';
import defaultsDeep from 'lodash.defaultsdeep';
import {
    extract_actblue_refcodes,
    get_metadata_url_params
} from '../helpers/parse_url_params';
import {
    get_iterable_ids,
    set_iterable_campaign_template_temporary_cookies,
} from '../helpers/iterable_helpers';
import { v4 as uuidv4 } from 'uuid';

/**
 * Fetches public client metadata from the forms domain.
 * @param {Object} data_hub_instance
 * @returns {Promise<Object>} The client metadata object.
 */
export async function load_client_public_metadata(data_hub_instance) {
    if (!data_hub_instance.forms_domain) {
        data_hub_instance.error_function?.('Error: no forms_domain', null);
        return {};
    }
    const endpoint = `${data_hub_instance.forms_domain}/api/get/client_metadata`;
    try {
        const response = await fetch_with_timeout(endpoint, {
            method: 'GET',
            credentials: 'include',
            timeout: 5000
        });
        if (response && response.ok) {
            const data = await response.json();
            if (data && typeof data === 'object') {
                return data;
            } else {
                data_hub_instance.error_function?.('Client metadata is not an object: ' + typeof data, null);
            }
        } else if (response) {
            data_hub_instance.error_function?.('Request to get client metadata failed: ' + response.status + response.statusText, null);
        } else {
            data_hub_instance.error_function?.('Request to get client metadata timed out', null);
        }
    } catch (error) {
        data_hub_instance.error_function?.('Error fetching client metadata: ' + error, null);
    }
    return {};
}

/**
 * Sets page metadata defaults and Iterable IDs on the data_hub instance.
 * @param {Object} data_hub_instance
 * @param {Object} config - The constructor config object.
 */
export function set_page_metadata_defaults(data_hub_instance, config) {
    if (!config.page_metadata || typeof config.page_metadata !== 'object') {
        config.page_metadata = {};
    }

    if (!data_hub_instance.page_metadata) {
        data_hub_instance.page_metadata = Object.assign({}, config.page_metadata || {});
    } else {
        data_hub_instance.page_metadata = defaultsDeep(data_hub_instance.page_metadata, config.page_metadata || {});
    }

    // Get ActBlue refcodes.
    data_hub_instance.page_metadata.refcodes = extract_actblue_refcodes(data_hub_instance.url_params);

    // If passed in config.refcodes use as priority defaultDeep
    if (config?.refcodes) {
        data_hub_instance.page_metadata.refcodes = defaultsDeep(data_hub_instance.page_metadata.refcodes, config.refcodes);
    }

    // Get additional metadata from URL parameters.
    get_metadata_url_params(data_hub_instance.url_params, data_hub_instance);

    // Set Iterable IDs.
    const iterable_ids = get_iterable_ids(config, data_hub_instance.url_params);
    data_hub_instance.iterableCampaignId = iterable_ids.iterableCampaignId;
    data_hub_instance.iterableTemplateId = iterable_ids.iterableTemplateId;

    set_iterable_campaign_template_temporary_cookies(data_hub_instance.iterableCampaignId, data_hub_instance.iterableTemplateId);

    if (document.referrer) {
        data_hub_instance.page_metadata.referer = document.referrer;
    }
    if (document.title) {
        data_hub_instance.page_metadata.page_title = document.title;
    }
    if (window.location.href) {
        data_hub_instance.page_metadata.page_url = window.location.href.split('?')[0];
    }

    data_hub_instance.page_metadata.pageview_uuid = uuidv4();
}
